/**
 * One data-free rendering of a governed page LayoutDocument (#2033).
 *
 * The database resolves page and widget authority. The host fetches data and
 * rechecks action/drill authority when invoked. This recipe owns only the page
 * hierarchy, responsive section geometry, and explicit states.
 */
import type { CSSProperties, ReactNode } from 'react'
import type { DescribedPage as GovernedDescribedPage, DescribedPageBlock, PageBlockState } from '@solum/experience'
import { PageShell, type PageWidth } from '../shell/PageShell'
import { PageSection } from '../shell/PageSection'
import { StateBoundary, type ExperienceState } from './StateBoundary'
import s from './PageComposition.module.css'

export interface PageCompositionProps {
  page: DescribedPage
  title: ReactNode
  eyebrow?: ReactNode
  summary?: ReactNode
  status?: ReactNode
  secondaryActions?: ReactNode
  primaryAction?: ReactNode
  width?: PageWidth
  locale?: 'en' | 'fr' | 'nl'
  /** Transport state belongs to the host. Omit after the page door answers. */
  state?: ExperienceState
  /** Called only for a server-ready block carrying a parsed descriptor. */
  renderBlock: (block: DescribedPageBlock) => ReactNode
  /**
   * The host's native operational tool, bound beside the governed content
   * (#2940, ROOT c.119063): rendered inside this ONE shell, BEFORE the
   * governed content and OUTSIDE its state boundary — so it stays usable while
   * that content is loading, absent, refused or failed, and the content keeps
   * its own diagnostic. It is not a second shell, a block kind or a page
   * grammar: configurable content is still the document's. Absent, the output
   * is exactly what it was.
   */
  children?: ReactNode
  testId?: string
}

function blockState(kind: PageBlockState, reason: string | null): ExperienceState {
  switch (kind) {
    case 'ready': return { kind: 'ready' }
    case 'loading': return { kind: 'loading', label: 'Loading this section…' }
    case 'empty': return { kind: 'empty', message: reason ?? 'Nothing to show yet.' }
    case 'partial': return { kind: 'partial', title: 'Some information is unavailable', message: reason }
    case 'offline': return { kind: 'offline', title: 'Offline', message: reason ?? 'This section will return when you reconnect.' }
    case 'error': return { kind: 'error', title: 'This section could not be shown', message: reason ?? 'The page description is invalid.' }
    case 'missing_install': return { kind: 'refused', title: 'Component not installed', message: reason ?? 'This page component is not installed.' }
    case 'entitlement_lapsed': return { kind: 'refused', title: 'No longer included', message: reason ?? 'This page component is not included in the current plan.' }
    case 'refused': return { kind: 'refused', title: 'Not available to you', message: reason ?? 'This page component is outside your reach.' }
  }
}

/* Two custom-property groups, both optional: a SECTION carries the track count
   and a BLOCK carries its span, and neither object sets the other's. */
type SpanStyle = CSSProperties & {
  '--page-cols-desktop'?: number
  '--page-cols-tablet'?: number
  '--page-cols-phone'?: number
  '--page-span-desktop'?: number
  '--page-span-tablet'?: number
  '--page-span-phone'?: number
}

export function PageComposition({
  page, title, eyebrow, summary, status, secondaryActions, primaryAction,
  width = 'full', locale = 'en', state, renderBlock, children, testId,
}: PageCompositionProps) {
  const pageState: ExperienceState = state ?? (
    page.state === 'error'
      ? { kind: 'error', title: 'This page could not be composed', message: page.reason ?? 'Its governed document is invalid.' }
      : page.resolved
        ? { kind: 'ready' }
        : { kind: 'empty', message: page.reason ?? 'No page composition is installed.' }
  )
  const bySection = new Map<string, DescribedPageBlock[]>()
  for (const block of page.blocks) {
    const group = bySection.get(block.section) ?? []
    group.push(block)
    bySection.set(block.section, group)
  }

  const draw = (
    block: DescribedPageBlock,
    span: { desktop: number; tablet: number; phone: number },
    cols: { desktop: number; tablet: number; phone: number },
  ) => {
    const style: SpanStyle = {
      '--page-span-desktop': Math.min(span.desktop, cols.desktop),
      '--page-span-tablet': Math.min(span.tablet, cols.tablet),
      '--page-span-phone': Math.min(span.phone, cols.phone),
    }
    const widget = (block.kind ?? 'widget') === 'widget'
    const content = block.reference?.kind === 'content' ? block.reference : null
    const usable = block.state === 'ready' && (!widget || block.descriptor !== null)
    const resolvedState = usable
      ? blockState('ready', null)
      : blockState(block.state === 'ready' ? 'error' : block.state, block.reason)
    return (
      <div key={block.id} className={s.block} style={style} data-page-block={block.id}>
        <StateBoundary state={resolvedState} testId={`page-block-${block.id}`}>
          {usable && content
            ? content.style === 'heading'
              ? <h3 className={s.contentHeading}>{content.text[locale] ?? content.text.en}</h3>
              : <p className={content.style === 'callout' ? s.contentCallout : s.contentHelper}
                   role={content.style === 'callout' ? 'note' : undefined}>
                  {content.text[locale] ?? content.text.en}
                </p>
            : usable ? renderBlock(block) : null}
        </StateBoundary>
      </div>
    )
  }

  return (
    <PageShell
      title={title} eyebrow={eyebrow} summary={summary} status={status}
      secondaryActions={secondaryActions} primaryAction={primaryAction}
      width={width} testId={testId}
    >
      {children != null && <div className={s.native} data-page-native="">{children}</div>}
      <StateBoundary state={pageState} testId={testId ? `${testId}-state` : undefined}>
        {page.document?.sections.map(section => {
          const blocks = bySection.get(section.id) ?? []
          if (blocks.length === 0) return null
          if (page.document?.schemaVersion === 3 && 'rows' in section) {
            const cols = { desktop: 4, tablet: 4, phone: 2 }
            const colStyle: SpanStyle = {
              '--page-cols-desktop': cols.desktop,
              '--page-cols-tablet': cols.tablet,
              '--page-cols-phone': cols.phone,
            }
            return (
              <PageSection key={section.id} title={section.label}
                summary={section.description} testId={`page-section-${section.id}`}>
                <div className={s.container}>
                  {section.rows.map(row => {
                    const rowBlocks = blocks.filter(block => block.row === row.id)
                    if (rowBlocks.length === 0) return null
                    return (
                      <div key={row.id} className={s.grid} style={colStyle}
                        data-page-definition-row={row.id} data-page-columns="4/4/2">
                        {row.columns.map(column => {
                          const columnBlocks = rowBlocks.filter(block => block.column === column.id)
                          if (columnBlocks.length === 0) return null
                          const columnStyle: SpanStyle = {
                            '--page-span-desktop': column.span.desktop,
                            '--page-span-tablet': column.span.tablet,
                            '--page-span-phone': column.span.phone,
                          }
                          return (
                            <div key={column.id} className={s.column} style={columnStyle}
                              data-page-definition-column={column.id}>
                              {columnBlocks.map(block => draw(block, { desktop: 1, tablet: 1, phone: 1 },
                                { desktop: 1, tablet: 1, phone: 1 }))}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </PageSection>
            )
          }
          if (!('columns' in section)) return null
          /* The AUTHORED track count, bound at last (ROOT 68189 item 2). It was
             parsed, stored, travelled in a solution — and the grid hardcoded
             4/4/2, so a section declaring two columns rendered four.

             v2 states all three profiles. v1 states ONE number, and the phone
             profile keeps the shipped cap of 2 rather than putting four widgets
             across a phone: `min(n, 2)` honours a section that asked for 1 and
             changes nothing for one that asked for 4. */
          const cols = typeof section.columns === 'number'
            ? { desktop: section.columns, tablet: section.columns, phone: Math.min(section.columns, 2) }
            : section.columns
          const colStyle: SpanStyle = {
            '--page-cols-desktop': cols.desktop,
            '--page-cols-tablet': cols.tablet,
            '--page-cols-phone': cols.phone,
          }
          return (
            <PageSection
              key={section.id} title={section.label}
              summary={section.description} testId={`page-section-${section.id}`}
            >
              <div className={s.container}>
                <ul className={s.grid} style={colStyle} data-page-composition-grid=""
                    data-page-columns={`${cols.desktop}/${cols.tablet}/${cols.phone}`}>
                  {blocks.map(block => {
                    const style: SpanStyle = {
                      '--page-span-desktop': Math.min(block.span.desktop, cols.desktop),
                      '--page-span-tablet': Math.min(block.span.tablet, cols.tablet),
                      '--page-span-phone': Math.min(block.span.phone, cols.phone),
                    }
                    const usable = block.state === 'ready' && block.descriptor !== null
                    const resolvedState = usable
                      ? blockState('ready', null)
                      : blockState(block.state === 'ready' ? 'error' : block.state, block.reason)
                    return (
                      <li key={block.id} className={s.block} style={style} data-page-block={block.id}>
                        <StateBoundary state={resolvedState} testId={`page-block-${block.id}`}>
                          {usable ? renderBlock(block) : null}
                        </StateBoundary>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </PageSection>
          )
        })}
      </StateBoundary>
    </PageShell>
  )
}

/* Re-declared beside the component whose props are written in it, so a host
   can type the data it hands this component from `@solum/ui` alone (#2406,
   design-sync kit pass: "hosts need @solum/experience to type their data").
   An alias, not a copy — the shape stays Experience's. */
export type DescribedPage = GovernedDescribedPage
